"use client";

import {
  TypeCheckoutConfiguration,
  TypePackageDetailItem,
  TypePackageModificationItem,
} from "@contentful/types";
import { faArrowRight } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CheckoutTotals } from "@yardzen/rest-api-client";
import { Badge } from "@yardzen/ui/components/badge";
import { Button } from "@yardzen/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@yardzen/ui/components/dialog";
import { H3 } from "@yardzen/ui/components/h3";
import { LineDivider } from "@yardzen/ui/components/line-divider";
import { Link } from "@yardzen/ui/components/link";
import { P } from "@yardzen/ui/components/p";
import { Spinner } from "@yardzen/ui/components/spinner";
import { VisuallyHidden } from "@yardzen/ui/components/visually-hidden";
import { cn } from "@yardzen/ui/lib/utils";
import { format } from "date-fns";
import Cookies from "js-cookie";
import Image from "next/image";
import { useState } from "react";

/**
 * Prepares an authenticated redirect to the client-account app.
 * This calls the auth server's /prepare endpoint to set up the redirect cookie,
 * then navigates to the /redirect endpoint.
 */
async function prepareAndRedirectToClientAccount(
  userId: string,
  authToken: string,
  destinationHost: string,
  destinationPath: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    // Call the auth server's prepare endpoint (proxied through /api/a)
    const prepareResponse = await fetch("/api/a/prepare", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        host: destinationHost,
        path: `/${destinationPath}`,
        parameters: { "onboard-redirect": "true" },
        userId,
      }),
      credentials: "include", // Important: include cookies
    });

    if (!prepareResponse.ok) {
      const errorText = await prepareResponse.text();
      console.error("Failed to prepare redirect:", {
        status: prepareResponse.status,
        error: errorText,
      });
      return {
        success: false,
        error: `Failed to prepare redirect: ${prepareResponse.status}`,
      };
    }

    // The prepare endpoint set the 'rd' cookie. Now redirect to /redirect
    return { success: true };
  } catch (error) {
    console.error("Error preparing redirect:", error);
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}

/**
 * Extracts the host from a URL string, handling both full URLs and localhost.
 */
function getHostFromUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.host;
  } catch {
    // If URL parsing fails, return as-is (might already be just a host)
    return url;
  }
}

export function PackageDetailItemCheckoutSuccess({
  email,
  packageDetailItem,
  checkoutConfiguration,
  packageModificationItem,
  paymentData,
  orderId,
  onboardingUrl,
  onboardingSetupReady,
  packageParam,
  purchasingExtras,
  userId,
  authToken,
  clientAccountUrl,
}: {
  email: string;
  packageDetailItem: TypePackageDetailItem<"WITHOUT_UNRESOLVABLE_LINKS">;
  checkoutConfiguration: TypeCheckoutConfiguration<"WITHOUT_UNRESOLVABLE_LINKS">;
  packageModificationItem?: TypePackageModificationItem<"WITHOUT_UNRESOLVABLE_LINKS">;
  paymentData: CheckoutTotals;
  /** Buyer-facing order reference: the PaymentIntent id for paid checkouts,
   * the synthetic legacy order id for $0 redemptions. */
  orderId: string;
  onboardingUrl?: string;
  packageParam?: string;
  onboardingSetupReady: boolean;
  purchasingExtras?: boolean;
  userId?: string;
  authToken?: string;
  clientAccountUrl?: string;
}) {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [redirectError, setRedirectError] = useState<string | null>(null);

  const handleExtrasRedirect = async () => {
    if (!userId || !authToken || !clientAccountUrl) {
      console.error(
        "Missing userId, authToken, or clientAccountUrl for extras redirect",
      );
      setRedirectError(
        "Unable to redirect. Please try again or contact support.",
      );
      return;
    }

    setIsRedirecting(true);
    setRedirectError(null);

    try {
      const destinationHost = getHostFromUrl(clientAccountUrl);
      const result = await prepareAndRedirectToClientAccount(
        userId,
        authToken,
        destinationHost,
        "tracker/finalize",
      );

      if (result.success) {
        Cookies.remove("yz-payment-intent");
        // Navigate to the redirect endpoint which will read the cookie and redirect
        window.location.href = "/api/a/redirect";
      } else {
        console.error("Failed to prepare extras redirect:", result.error);
        setRedirectError(
          result.error || "Failed to prepare redirect. Please try again.",
        );
        setIsRedirecting(false);
      }
    } catch (error) {
      console.error("Error during extras redirect:", error);
      setRedirectError("An unexpected error occurred. Please try again.");
      setIsRedirecting(false);
    }
  };
  const currentDate = new Date();
  const { checkoutImage } = packageDetailItem?.fields || {};
  let fullPackageName = packageModificationItem
    ? packageModificationItem?.fields?.name
    : packageDetailItem?.fields.fullPackageName;

  if (fullPackageName && !fullPackageName.includes("design")) {
    fullPackageName = `${fullPackageName} Design`;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const preventClose = (event: any) => {
    event?.preventDefault();
  };

  function addOnName(name: string) {
    const addOnsContentful = packageDetailItem?.fields.addOnItems;
    const addOn = addOnsContentful?.find(
      (addOn) => addOn?.fields.stripeProductId === name,
    );

    const addOnFallbackNames = {
      "exterior-upsell-front": "Exterior Design",
      "exterior-upsell-back": "Exterior Design",
      "exterior-upsell-full": "Exterior Design",
      "expedited-upgrade": "Expedited Delivery",
      "hoa-extra-revision": "HOA Support",
    };

    const nameReturn = addOn?.fields.heading
      ? addOn?.fields.heading
      : addOnFallbackNames[name as keyof typeof addOnFallbackNames];

    return nameReturn;
  }
  const getPackagePill = () => {
    if (!packageParam) {
      return;
    }
    if (packageParam.includes("essential")) {
      return "Essential";
    }
    if (packageParam.includes("signature")) {
      return "Signature";
    }
    if (packageParam.includes("classic")) {
      return "Classic";
    } else {
      return;
    }
  };
  const packagePill = getPackagePill();
  function getDiscountAmount(paymentData: CheckoutTotals): number | null {
    if (paymentData.discount && !!paymentData.discount.discount) {
      if (paymentData.discount.type === "fixed")
        return paymentData.discount.discount;
      if (paymentData.discount.type === "percentage")
        return (
          Math.round(
            (((paymentData.discount.discount / 100) * paymentData.subTotal) /
              100) *
              100,
          ) / 100
        );
    }

    return null;
  }

  const discountAmount = getDiscountAmount(paymentData);
  const subTotal = paymentData.subTotal / 100;
  const taxRate = paymentData.taxAmount
    ? Number(
        (
          (paymentData.taxAmount / 100 / (subTotal - (discountAmount || 0))) *
          100
        ).toFixed(2),
      )
    : 0;

  return (
    <Dialog open>
      <DialogContent
        onEscapeKeyDown={preventClose}
        onPointerDownOutside={preventClose}
        className="w-full max-w-[700px] !gap-2 rounded-lg border-0 p-4 max-sm:max-h-[95vh] max-sm:overflow-y-auto"
        dialogOverlayClassName="bg-[#C1DA2933]"
        dialogCloseClassName="hidden"
      >
        <VisuallyHidden>
          <DialogTitle>
            {checkoutConfiguration.fields.thankYouHeader ??
              "Thank You for Your Purchase!"}
          </DialogTitle>
        </VisuallyHidden>
        {!email && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-white bg-opacity-75">
            <Spinner />
          </div>
        )}
        {email && (
          <>
            <div className="flex w-full flex-col items-center text-center">
              <span role="img" aria-label="celebration" className="text-4xl">
                🎉
              </span>
              <H3 className="text-4xl tracking-normal">
                {checkoutConfiguration.fields.thankYouHeader ??
                  "Thank You for Your Purchase!"}
              </H3>
              {checkoutConfiguration.fields.thankYouSubheading && (
                <P noSpacing className="font-display">
                  {checkoutConfiguration.fields.thankYouSubheading}
                </P>
              )}
            </div>
            <div className="w-full overflow-auto p-4 sm:max-h-[calc(95vh-200px)] md:p-8">
              <div className="flex w-full flex-col gap-2 sm:flex-row">
                <div className="sm:flex-basis-1/2 bg-brand-beige-lighter flex w-full flex-col gap-2 rounded-lg sm:w-1/2">
                  <div className="w-full">
                    {checkoutImage?.fields.image?.fields.file?.url && (
                      <Image
                        src={`https:${checkoutImage.fields.image.fields.file.url}`}
                        alt={checkoutImage?.fields.description ?? ""}
                        height={600}
                        width={600}
                        className="h-auto w-full rounded-t-lg object-fill"
                      />
                    )}
                  </div>
                  <div className="flex w-full flex-col gap-2 px-4 pb-4 pt-2">
                    <div className="mb-2 flex flex-row gap-2">
                      <span className="font-display text-xl">
                        {fullPackageName}
                      </span>
                      {packagePill && (
                        <Badge variant="zest">{packagePill}</Badge>
                      )}
                    </div>
                    <LineItem
                      name={fullPackageName ?? "Design Package"}
                      price={paymentData.product.price / 100}
                    />
                    {paymentData.appliedAddons &&
                      paymentData.appliedAddons.map((addon, i) => {
                        const isFreeAddon =
                          paymentData.discount?.freeAddon === addon.key;
                        return (
                          <LineItem
                            key={`pdics-${addon.id}-${i}`}
                            className="text-brand-primary-regular"
                            // TODO need to get the name from Contentful
                            name={`+ ${addOnName(addon.key)}`}
                            price={isFreeAddon ? 0 : addon.price / 100}
                          />
                        );
                      })}
                    {discountAmount && (
                      <LineItem
                        name={"Discount"}
                        classNamePrice="text-success-main"
                        price={-discountAmount}
                      />
                    )}
                    {!!paymentData.taxAmount && !!taxRate && (
                      <LineItem
                        name={`Estimated Tax (${taxRate}%)`}
                        price={paymentData.taxAmount / 100}
                      />
                    )}
                    <LineDivider
                      className="h-[1px] bg-black opacity-20"
                      orientation="horizontal"
                    />
                    <LineItem name={"Total"} price={paymentData.total / 100} />
                  </div>
                </div>
                <div className="sm:flex-basis-1/2 flex w-full flex-col justify-between gap-2 sm:w-1/2">
                  <div className="flex w-full flex-col items-center justify-between gap-2 sm:gap-4">
                    <div className="flex w-full flex-col px-4 py-2">
                      <span className="text-xxs w-full opacity-50">
                        Confirmation email sent to:
                      </span>
                      <div className="w-full break-all text-sm">{email}</div>
                    </div>
                    <div className="flex w-full flex-col px-4 py-2">
                      <span className="text-xxs w-full opacity-50">
                        Order ID:
                      </span>
                      <div className="w-full break-all" data-testid="order-id">
                        {orderId}
                      </div>
                    </div>
                    <div className="flex w-full flex-col px-4 py-2">
                      <span className="text-xxs w-full opacity-50">
                        Order Date:
                      </span>
                      <div className="w-full">
                        {format(currentDate, "MMMM dd, yyyy")}
                      </div>
                    </div>
                  </div>
                  <div>
                    {purchasingExtras ? (
                      // Extras checkout - redirect to client-account tracker/finalize
                      <>
                        <Button
                          className="mx-2 !mb-0 w-full"
                          onClick={handleExtrasRedirect}
                          disabled={isRedirecting}
                        >
                          {isRedirecting ? (
                            <>
                              <Spinner className="mr-2 h-4 w-4" />
                              Redirecting...
                            </>
                          ) : (
                            <>
                              Review My Design{" "}
                              <FontAwesomeIcon icon={faArrowRight} />
                            </>
                          )}
                        </Button>
                        {redirectError && (
                          <P className="mt-2 text-center text-sm text-red-600">
                            {redirectError}
                          </P>
                        )}
                      </>
                    ) : onboardingUrl && onboardingSetupReady ? (
                      <Button className="mx-2 !mb-0 w-full" asChild>
                        <Link
                          className="font-body text-brand-primary-darker mx-0 mb-4 inline-flex whitespace-nowrap bg-[#e9eec6] px-6 py-3 font-semibold leading-3"
                          href={onboardingUrl}
                          onClick={() => Cookies.remove("yz-payment-intent")}
                        >
                          {checkoutConfiguration.fields.redirectUrlText}{" "}
                          <FontAwesomeIcon icon={faArrowRight} />
                        </Link>
                      </Button>
                    ) : (
                      <Button className="mx-2 !mb-0 w-full" disabled>
                        {checkoutConfiguration.fields.redirectUrlText}{" "}
                        <FontAwesomeIcon icon={faArrowRight} />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              {!purchasingExtras &&
                (!onboardingUrl || !onboardingSetupReady) && (
                  <div className="flex items-center justify-center gap-2 pb-2 pt-4">
                    <Spinner className="h-4 w-4" />
                    <P noSpacing className="text-sm opacity-70">
                      Finishing setting up your project...
                    </P>
                  </div>
                )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );

  function LineItem({
    name,
    price,
    className,
    classNamePrice,
  }: {
    name: string;
    price: number;
    className?: string;
    classNamePrice?: string;
  }) {
    return (
      <div className="space-between flex w-full flex-row justify-between gap-2">
        <div
          className={cn(
            "font-base text-brand-moss-greens-90 text-md",
            className,
          )}
        >
          {name}
        </div>
        <div
          className={cn(
            "font-base text-brand-secondary-dark text-md font-semibold",
            classNamePrice,
          )}
        >
          ${Number(price).toFixed(2)}
        </div>
      </div>
    );
  }
}
